import {
  BottomSheetModal,
  BottomSheetModalProps,
  BottomSheetModalProvider,
} from '@gorhom/bottom-sheet'
import { ParamListBase, useTheme } from '@react-navigation/native'
import * as React from 'react'
import { Portal } from 'react-native-paper'

import type {
  BottomSheetDescriptorMap,
  BottomSheetNavigationConfig,
  BottomSheetNavigationHelpers,
  BottomSheetNavigationProp,
  BottomSheetNavigationState,
} from '@/components/navigation/bottomSheet/types'

type BottomSheetModalScreenProps = BottomSheetModalProps & {
  navigation: BottomSheetNavigationProp<ParamListBase>
}

const BottomSheetModalScreen = ({
  index,
  navigation,
  enableDynamicSizing,
  children,
  ...props
}: BottomSheetModalScreenProps) => {
  const ref = React.useRef<BottomSheetModal>(null)
  const lastIndexRef = React.useRef(index)

  // Present on mount.
  React.useEffect(() => {
    ref.current?.present()
  }, [])

  const isMounted = React.useRef(true)
  React.useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  React.useEffect(() => {
    if (index != null && lastIndexRef.current !== index) {
      ref.current?.snapToIndex(index)
    }
  }, [index])

  const onChange = React.useCallback(
    (newIndex: number) => {
      lastIndexRef.current = newIndex
      if (newIndex >= 0) {
        navigation.snapTo(newIndex)
      }
    },
    [navigation]
  )

  const onDismiss = React.useCallback(() => {
    // BottomSheetModal will call onDismiss on unmount, be we do not want that since
    // we already popped the screen.
    if (isMounted.current) {
      navigation.goBack()
    }
  }, [navigation])

  return (
    <BottomSheetModal
      enableDynamicSizing={enableDynamicSizing}
      index={index}
      onChange={onChange}
      onDismiss={onDismiss}
      ref={ref}
      {...props}
    >
      {children}
    </BottomSheetModal>
  )
}

const DEFAULT_SNAP_POINTS = ['66%']

type Props = BottomSheetNavigationConfig & {
  state: BottomSheetNavigationState<ParamListBase>
  navigation: BottomSheetNavigationHelpers
  descriptors: BottomSheetDescriptorMap
}

// eslint-disable-next-line import/prefer-default-export
export const BottomSheetView = ({ state, descriptors }: Props) => {
  const { colors } = useTheme()
  const themeBackgroundStyle = React.useMemo(
    () => ({
      backgroundColor: colors.card,
    }),
    [colors.card]
  )
  const themeHandleIndicatorStyle = React.useMemo(
    () => ({
      backgroundColor: colors.border,
    }),
    [colors.border]
  )

  // Avoid rendering provider if we only have one screen.
  const shouldRenderProvider = React.useRef(false)
  shouldRenderProvider.current =
    shouldRenderProvider.current || state.routes.length > 1

  const firstScreen = descriptors[state.routes[0].key]

  return (
    <>
      {firstScreen.render()}
      <Portal>
        {shouldRenderProvider.current && (
          <BottomSheetModalProvider>
            {state.routes.slice(1).map((route) => {
              const { options, navigation, render } = descriptors[route.key]

              const {
                index,
                backgroundStyle,
                handleIndicatorStyle,
                snapPoints,
                enableDynamicSizing,
                ...sheetProps
              } = options

              return (
                <BottomSheetModalScreen
                  backgroundStyle={[themeBackgroundStyle, backgroundStyle]}
                  enableDynamicSizing={enableDynamicSizing}
                  handleIndicatorStyle={[
                    themeHandleIndicatorStyle,
                    handleIndicatorStyle,
                  ]}
                  // Make sure index is in range, it could be out if snapToIndex is persisted
                  // and snapPoints is changed.
                  index={Math.min(
                    route.snapToIndex ?? index ?? 0,
                    snapPoints != null ? snapPoints.length - 1 : 0
                  )}
                  key={route.key}
                  navigation={navigation}
                  snapPoints={
                    snapPoints == null && !enableDynamicSizing
                      ? DEFAULT_SNAP_POINTS
                      : snapPoints
                  }
                  {...sheetProps}
                >
                  {render()}
                </BottomSheetModalScreen>
              )
            })}
          </BottomSheetModalProvider>
        )}
      </Portal>
    </>
  )
}
